"use client";

import { useEffect, useRef } from "react";
import styles from "./page.module.css";
import { Pagination } from "@/components/pagination/pagination";
import { AdminAgencyApplicationsResponse } from "./types";
import ApplicationModal from "./modals/application-modal";
import { useApplicationStore } from "./applications-store";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useScrollLock } from "@/hooks/use-scroll-lock";

export default function AgenciesApplicationsLayout({
  applicationsResponse,
}: {
  applicationsResponse: AdminAgencyApplicationsResponse;
}) {
  const initState = useApplicationStore((state) => state.initState);
  const isLoading = useApplicationStore((state) => state.isLoading);
  const applications = useApplicationStore((state) => state.applications);
  const number_of_pages = useApplicationStore((state) => state.number_of_pages);
  const page_number = useApplicationStore((state) => state.page_number);
  const total_count = useApplicationStore((state) => state.total_count);
  const isApplicationModalOpen = useApplicationStore(
    (state) => state.isApplicationModalOpen,
  );
  const openApplication = useApplicationStore((state) => state.openApplication);
  const closeApplication = useApplicationStore(
    (state) => state.closeApplication,
  );
  const query = useApplicationStore((state) => state.query);
  const status = useApplicationStore((state) => state.status);
  const setQuery = useApplicationStore((state) => state.setQuery);
  const setStatus = useApplicationStore((state) => state.setStatus);
  const fetchPage = useApplicationStore((state) => state.fetchPage);
  const { userData } = useUserAuthDataContext();
  const modalRef = useRef<HTMLDivElement>(null);
  useScrollLock(isApplicationModalOpen);

  useEffect(() => {
    initState(applicationsResponse);
  }, []);

  return (
    <main className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <h1>Agency Applications</h1>
        <p className={styles.subtitle}>
          Review and manage agency upgrade requests
        </p>
      </header>

      {/* Filters */}
      <section className={styles.filters}>
        <input
          type="text"
          placeholder="Search by agency, applicant or user id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={styles.search}
        />

        <select
          className={styles.select}
          value={status ?? ""}
          onChange={(e) => {
            const value = e.target.value as
              | "pending"
              | "approved"
              | "rejected"
              | "";

            setStatus(value === "" ? null : value);
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <button
          className={styles.searchBtn}
          onClick={async () => {
            await fetchPage(userData.sessionId, 1);
          }}
        >
          Search
        </button>
      </section>

      {/* List */}
      <section className={styles.list}>
        {isLoading && <div className={styles.loading}>Loading…</div>}

        {!isLoading && applications?.length === 0 && (
          <div className={styles.empty}>No applications found.</div>
        )}

        {!isLoading &&
          applications?.map((item) => (
            <div
              key={item.request_id}
              className={styles.card}
              onClick={(e) => {
                e.preventDefault();
                openApplication(item.request_id, userData.sessionId);
              }}
            >
              <div className={styles.cardLeft}>
                <h3>{item.agency_name}</h3>
                <span className={styles.applicant}>{item.full_name}</span>
                <span className={styles.sub}>{item.user_sub}</span>
              </div>

              <div className={styles.cardRight}>
                <span className={`${styles.status} ${styles[item.status]}`}>
                  {item.status}
                </span>
                <span className={styles.date}>
                  {new Date(item.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
      </section>

      {/* Pagination */}
      {applications && number_of_pages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            total={total_count}
            pageSize={32}
            currentPage={page_number}
            onPageChange={async (number) => {
              if (number === page_number) return;
              fetchPage(userData.sessionId, number);
            }}
          />
        </div>
      )}
      {isApplicationModalOpen != null && (
        <ApplicationModal dialogRef={modalRef} />
      )}
    </main>
  );
}
